import { useParams, Link, useHistory, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { getRandomUid } from 'utilities/uuid.service';
import * as AppPics from 'assets/images';
import useGetOneEpisode from 'hooks/API/GET/useGetOneEpisode';
import Joi from 'joi';
import { AppTextField, AppUploadBlock, AppButton, AppRadioBox, AppSwitchBox } from 'components';
import useCreateOrUpdateEpisode from 'hooks/API/POST/useCreateOrUpdateEpisode';
import { notifier } from 'components/Toaster';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';

const actionsConfig = {
  open: false,
  data: {},
  Header: null,
  Body: null,
  isDeleteOperation: false,
};

function ManageEpisode() {
  const { podcastId, episodeId } = useParams();
  const [episodeName, setEpisodeName] = useState('');
  const [episodeSubtitle, setEpisodeSubtitle] = useState('');
  const [episodeArtists, setEpisodeArtists] = useState('');

  const [episodeUrl, setEpisodeUrl] = useState('');
  const [episodeImage, setEpisodeImage] = useState(null);
  const [episodeImageFullPath, setEpisodeImageFullPath] = useState(null);
  const [deletedImage, setDeletedImage] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [referenceKey, setReferenceKey] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  const [validationErrorServer, setValidationErrorServer] = useState({});
  const [cachedArtist, setCachedArtist] = useState('');
  const [isParent, setIsParent] = useState(false);
  const [actions, setActions] = useState({ ...actionsConfig });
  const [published, setPublished] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [genreId, setGenreId] = useState('');

  const isEditMode = episodeId !== 'add';
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectBack = params.get('redirect_back');

  const handleGoBack = () => {
    if (redirectBack) {
      history.push(decodeURIComponent(redirectBack));
    } else {
      history.goBack(); // fallback
    }
  };

  const { mutate, isLoading } = useCreateOrUpdateEpisode();
  const { data: episodeData } = useGetOneEpisode({
    podcastId,
    episodeId,
    enabled: !!episodeId && episodeId !== 'add',
  });

  useEffect(() => {
    if (isEditMode && episodeData?.data) {
      setCachedArtist(episodeData?.data?.artists);
    }
  }, [isEditMode, episodeData?.data]);

  useEffect(() => {
    if (episodeId === 'add') {
      clearData();
      return;
    }

    if (redirectBack && window.history.length <= 2) {
      window.history.replaceState(null, '', location.pathname + location.search);
      window.history.pushState(null, '', decodeURIComponent(redirectBack));
      window.history.pushState(null, '', location.pathname + location.search);
    }

    if (episodeData?.data) {
      const d = episodeData.data;

      setEpisodeName(d.name || '');
      setEpisodeSubtitle(d.subtitle || '');
      setEpisodeArtists(d.artists || '');

      setEpisodeUrl(d.podcastEpisodeUrl || '');
      setEpisodeImage(d.podcastCoverImage || null);
      setEpisodeImageFullPath(d.coverImg || null);
      setIsFeatured(d.featured || false);
      setDeletedImage(false);
      setReferenceKey(d.referenceKey || getRandomUid());
      setPublished(d.isPublished || null);
      setIsSaved(true);
    }
  }, [episodeId, episodeData]);

  const handleImageChange = ({ file, uploadedData }) => {
    setEpisodeImage(uploadedData?.file);
    setEpisodeImageFullPath(uploadedData?.basePath + uploadedData?.file);
    setDeletedImage(false);
  };

  const handleSwitchKeyDown = (e) => {
    // Handle Space and Enter keys to toggle the switch
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault(); // Prevent default behavior (like form submission or page scroll)
      setIsFeatured(!isFeatured); // Toggle the state
    }
  };

  const handleSave = () => {
    setFormErrors([]);
    const payload = {
      episodeName: episodeName.trim(),
      episodeSubtitle: episodeSubtitle.trim(),
      episodeArtists: episodeArtists.trim(),

      podcastEpisodeUrl: episodeUrl.trim(),
      podcastCoverImage: deletedImage ? null : episodeImage,
      featured: isFeatured,
      isParent,
      canPublish: published,
    };
    if (deletedImage) {
      payload.imageDeleted = true;
    }

    const validation = episodeSchema.validate(payload, { abortEarly: false, allowUnknown: true });
    if (validation.error) {
      setFormErrors(validation.error.details);

      return;
    }
    mutate(
      {
        ...payload,
        podcastId,
        episodeId: episodeId !== 'add' ? episodeId : undefined,
      },
      {
        onSuccess(response) {
          const message = response?.data?.data?.message;
          notifier.success(message);
          setGenreId(response?.data?.data?.record);
          setIsSaved(true);
          // setTimeout(() => {
          //   handleGoBack();
          // }, 2000);
          // clearData();
        },
        onError(error) {
          const message = error?.response?.data?.message;
          const validationErrors = error?.response?.data?.errors;
          notifier.error(message);
          setValidationErrorServer(validationErrors || {});
        },
      }
    );
  };

  const handlePublish = (value) => {
    setFormErrors([]);
    if (disableSubmit) return;

    const payload = {
      episodeName: episodeName.trim(),
      episodeSubtitle: episodeSubtitle.trim(),
      episodeArtists: episodeArtists.trim(),

      podcastEpisodeUrl: episodeUrl.trim(),
      podcastCoverImage: deletedImage ? null : episodeImage,
      featured: isFeatured,
      isParent,
      canPublish: value,
      podcastId,
      episodeId: episodeId && episodeId !== 'add' ? episodeId : genreId,
    };    

    mutate(payload, {
      onError(failData) {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message;
        const validationErrors = errorData?.errors;

        setValidationErrorServer(validationErrors || {});
        notifier.error(errorMessage);
      },

      onSuccess() {
        notifier.success(`Podcast Episode ${value ? 'published' : 'unpublished'} successfully`);
        setTimeout(() => {
          handleGoBack();
        }, 2000);
      },
    });
  };

  const clearData = () => {
    setEpisodeName('');
    setEpisodeSubtitle('');
    setEpisodeArtists('');

    setEpisodeUrl('');
    setEpisodeImage(null);
    setEpisodeImageFullPath(null);
    setIsFeatured(false);
    setDeletedImage(false);
    setReferenceKey(getRandomUid());
    setPublished(null);
  };

  const errors = checkForErrors(formErrors);

  const handleDeleteEpisodeImage = () => {
    setEpisodeImage(null);
    setEpisodeImageFullPath(null);
    setDeletedImage(true);
    setReferenceKey(getRandomUid());
    notifier.success('Episode image deleted successfully');
  };

  const handleDeleteEpisodemageConfirm = () => {
    setActions({
      open: true,
      Header: 'Delete Image',
      Body: 'Are you sure you want to delete this image?',
      isDeleteOperation: true,
      data: {
        type: 'EPISODE_IMAGE',
      },
    });
  };

  const handlePublishToggleConfirm = (newValue) => {
    setActions({
      open: true,
      Header: 'Confirm Publish Status',
      Body: `Are you sure you want to ${newValue ? 'publish' : 'unpublish'} this episode?`,
      isDeleteOperation: false,
      data: {
        type: 'PUBLISH_TOGGLE',
        value: newValue,
      },
    });
  };

  const handleActionsConfirm = () => {
    const { type, value } = actions.data || {};
    if (type === 'EPISODE_IMAGE') {
      handleDeleteEpisodeImage();
    }
    if (type === 'PUBLISH_TOGGLE') {
      handlePublish(value);
    }
    setActions({ ...actionsConfig });
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleActionsConfirm}
      />
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleGoBack}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>
          <div
            className="tab__content
        ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast Home', to: '/podcast' },
                  { label: 'Podcasts', to: `/podcast/list` },
                  { label: 'Podcast Episode', to: `/podcast/list` },
                  { label: 'Podcast Episodes', to: `/podcast/${podcastId}/episodes` },
                ]}
                current={episodeId === 'add' ? 'Add Episode' : 'Update Episode'}
              />
            </div>

            {/* <div className="mainWraper"> */}
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="category-form">
                  {episodeId !== 'add' ? (
                    <h2 className="prime-head p-0 boldtitle">Update Episode</h2>
                  ) : (
                    <h2 className="prime-head p-0 boldtitle">Add Episode</h2>
                  )}

                  {/* Row 1: Episode Name & Subtitle */}
                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{ label: 'Episode Name *' }}
                        inputProps={{
                          value: episodeName,
                          onChange: (e) => setEpisodeName(e.target.value),
                          placeholder: 'Episode Name',
                          tabIndex: 1,
                        }}
                        fieldErrors={errors['episodeName']}
                      />
                      {errors.episodeName && (
                        <div className="text-danger">{errors['episodeName'].errorMessage}</div>
                      )}
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{ label: 'Subtitle' }}
                        inputProps={{
                          value: episodeSubtitle,
                          onChange: (e) => setEpisodeSubtitle(e.target.value),
                          placeholder: 'Subtitle',
                          tabIndex: 2,
                        }}
                        fieldErrors={errors['subtitle']}
                      />
                    </div>
                  </div>

                  {/* Row 2: Artists & Episode URL */}
                  <div className="row mt-4">
                    <div className="col-lg-6 d-flex flex-row align-items-center">
                      <div className={isEditMode && 'w-100'}>
                        <AppTextField
                          labelProps={{ label: 'Artists' }}
                          inputProps={{
                            value: episodeArtists,
                            onChange: (e) => setEpisodeArtists(e.target.value),
                            placeholder: 'Artists',
                            // disabled: useParentEpisodeArtist, // new state
                            tabIndex: 3,
                            disabled: isParent,
                          }}
                          fieldErrors={errors['artists']}
                        />
                      </div>
                      {!isEditMode && (
                        <div className="ml-4 d-flex flex-column">
                          <label className="form-label invisible">Placeholder</label>
                          <AppSwitchBox
                            inputProps={{
                              checked: isParent, // new state
                              onChange: (e) => {
                                const checked = e.target.checked;
                                setIsParent(checked); // toggle state

                                // when ON, clear the episodeArtists field
                                setEpisodeArtists(checked ? '' : episodeArtists);
                              },
                              tabIndex: 4,
                              'aria-label': 'Toggle parent episode artist',
                            }}
                            labelProps={{ label: 'Choose parent Artist' }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{ label: 'Episode URL' }}
                        inputProps={{
                          value: episodeUrl,
                          onChange: (e) => setEpisodeUrl(e.target.value),
                          placeholder: 'Optional URL',
                          tabIndex: 4,
                        }}
                      />
                    </div>
                  </div>

                  {/* Row 3: Upload Image & Featured Status */}
                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppUploadBlock
                        labelProps={{ label: 'Episode Cover Image' }}
                        props={{
                          referenceKey: 'PODCAST_EPISODES',
                          isImage: true,
                          isPublic: true,
                          uploadCallback: (val) => {
                            setDisableSubmit(val);
                          },
                          handleChange: (data) => handleImageChange(data),
                          fileData: {
                            fullPath: deletedImage ? null : episodeImageFullPath,
                          },
                          clearFile: deletedImage,
                          tabIndex: 5,
                        }}
                      />
                      {(episodeImage || episodeImageFullPath) && !deletedImage && (
                        <button
                          className="actions-icons mx-2 text-danger mt-2"
                          title="Delete Episode Image"
                          onClick={handleDeleteEpisodemageConfirm}
                          type="button"
                        >
                          <img src={AppPics.DeleteSvg} alt="Delete" />
                        </button>
                      )}
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <label className="label d-block">
                        Featured?
                        <span>
                          <small className="text-muted">
                            {' '}
                            (Featured Episodes are currently not in use.)
                          </small>
                        </span>
                      </label>
                      <div className="d-flex flex-row">
                        <AppSwitchBox
                          inputProps={{
                            checked: isFeatured,
                            onChange: (e) => setIsFeatured(e.target.checked),
                            onKeyDown: handleSwitchKeyDown,
                            tabIndex: 6,
                            'aria-label': 'Toggle featured status',
                          }}
                          labelProps={{ label: 'Show as Featured' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="row mt-4">
                    <div className="col-12 d-flex justify-content-end">
                      <AppButton
                        type="submit"
                        variantClass="btn-primary mx-3"
                        style={{ minWidth: '1.5rem' }}
                        onClick={() => handlePublishToggleConfirm(!published)}
                        disabled={!isSaved || isLoading}
                        tabIndex={6}
                      >
                        {published ? 'Unpublish Episode' : 'Publish Episode'}
                      </AppButton>
                      <AppButton
                        type="submit"
                        variantClass="btn-primary mx-3"
                        style={{ minWidth: '1.5rem' }}
                        onClick={handleSave}
                        disabled={disableSubmit || isLoading}
                        tabIndex={6}
                      >
                        {isLoading ? 'Saving...' : 'Save Episode'}
                      </AppButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const episodeSchema = Joi.object({
  episodeName: Joi.string().trim().min(1).max(100).required().messages({
    'string.base': 'Episode name must be a string.',
    'string.empty': 'Episode name cannot be empty.',
    'string.min': 'Episode name must be at least 1 character long.',
    'string.max': 'Episode name must be at most 100 characters long.',
    'any.required': 'Episode name is required.',
  }),
});

const checkForErrors = (errorsArray = []) => {
  const map = {};
  errorsArray.forEach((err) => {
    map[err.context.key] = {
      errorMessage: err.message,
    };
  });
  return map;
};

export default ManageEpisode;
